import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC } from "react";
import { NEUTRAL } from "../../../../theme/tokens";

const DEFAULT_HEIGHT_PX = 8;
const PERCENT = 100;

export type SystemStackedBarSegment = {
	id: string;
	/** The segment's share, in the same unit as every other segment and `max`. Zero draws nothing. */
	value: number;
	/** Fill colour — a categorical colour, so the segment matches its legend entry. */
	color: string;
};

export type SystemStackedBarProps = {
	/** Drawn left to right in this order. */
	segments: readonly SystemStackedBarSegment[];
	/**
	 * What a full track stands for. Omit and the segments fill the track; pass a larger total (the
	 * biggest row of a ranked list) so rows compare against each other and the rest stays empty track.
	 */
	max?: number;
	/** Bar height in px. Default 8. */
	height?: number;
	/** What the bar says, for a screen reader ("1,604 h long shifts, 19,075 h no PBJ job"). */
	ariaLabel: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * **Parts of a whole on one bar**: each segment's width is its share of the total, side by side in a
 * single rounded track — the composition behind a headline number (hours to review by cause), or one
 * row of a ranked list when every row shares the same `max`. Use `SystemLinearMeter` for one value
 * against a maximum. Presentational: `role="img"` with the caller's label; the legend lives beside it.
 */
export const SystemStackedBar: FC<SystemStackedBarProps> = ({ segments, max, height, ariaLabel, dataTestId, sx }) => {
	// DEFAULT: an 8px bar, the same weight as `SystemLinearMeter`.
	const barHeight = height ?? DEFAULT_HEIGHT_PX;
	const total = segments.reduce((sum, segment) => sum + segment.value, 0);
	// DEFAULT: with no `max` the segments fill the whole track.
	const scale = max ?? total;
	if (segments.some((segment) => segment.value < 0)) throw new Error("SystemStackedBar segments can't be negative");
	if (scale < total) throw new Error(`SystemStackedBar max ${scale} is below the segments' total ${total}`);
	return (
		<Box
			role="img"
			aria-label={ariaLabel}
			data-testid={dataTestId}
			sx={[
				{ display: "flex", width: "100%", height: barHeight, borderRadius: barHeight / 2, overflow: "hidden", backgroundColor: NEUTRAL[150] },
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{scale > 0 &&
				segments
					.filter((segment) => segment.value > 0)
					.map((segment) => (
						<Box
							key={segment.id}
							data-testid={dataTestId ? `${dataTestId}-${segment.id}` : undefined}
							sx={{ width: `${(segment.value / scale) * PERCENT}%`, backgroundColor: segment.color, flexShrink: 0 }}
						/>
					))}
		</Box>
	);
};

export default SystemStackedBar;
